import * as React from "react";
import { ControlProps } from "react-select/lib/components/Control";
import { MenuListComponentProps } from "react-select/lib/components/Menu";
import { Actions as ActionsBase } from "@common/components/actions";
import { ClickAwayListener } from "@common/components/ClickAwayListener";
import { Flex } from "@common/components/Flex";
import { DatasheetEditorHeaderProps } from "./header.types";
import { useController } from "./header.controller";
import { Button } from "@common/components/Button";
import { MessageBox } from "@common/components/MessageBox";
import Dropdown, { components, DROPDOWN_NO_VALUE } from "@common/containers/fields/Dropdown";

import { useDatasheetFormatManagementController } from "./format-management.controller";
import { FormatFieldSelector } from "@common/components/format-field-selector";
import { FormatColumnKey } from "@common/components/format-field-selector/components/formats/formats.types";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { DATASHEETS_MODULE_NAME } from "@modules/datasheets/constants/common";
import { trDts } from "@modules/datasheets/utils/i18n";

import "./header.styles.scss";

export const Header: React.FC<DatasheetEditorHeaderProps> = (props) => {
  const {
    handleUpnChange,
    upns,
    actions,
    messageProps,
    closeMessage,
    showErrorMessage,
    datasheetFormatsManagementOpened,
    getMenuPortalStyles,
    handleDatasheetFormatsManagementOpen,
    handleDatasheetFormatsManagementClose,
    handleFormatAsTheDefaultDropdownClose,
    handleFormatAsTheDefaultDropdownChange,
  } = useController(props);
  const {
    upn,
    getFoldersForDatasheet,
    getDatasheetFolderFormats,
    formatOnly,
    forceOpenFormatPicker,
    onFormatPickerClose,
    showFormatPicker = true,
    onSetFolderDefaultFormat,
    onSetPedigreeDefaultFormat,
    canSetFolderDefault,
    context,
    templateOptionOn,
    initialSelectedFormat,
    reloadRecentlyUsedKey,
  } = props;

  const {
    folders,
    folderFormats,
    isFoldersLoading,
    selectedFolder,
    selectedFormat,
    handleSelectedFolderChange,
    handleFormatSelect,
    isFolderFormatsLoading,
    recentlyUsedFormats,
    sessionFormats,
    isRecentlyUsedLoading,
  } = useDatasheetFormatManagementController({
    getFolders: getFoldersForDatasheet,
    getFormats: getDatasheetFolderFormats,
    onSubmit: (payload) => {
      handleDatasheetFormatsManagementClose();
      props.onDatasheetFormatsSubmit(payload);
      handleDatasheetFormatsManagementClose();
    },
    showErrorMessage,
    context: context ?? 1,
    pickerOpened: datasheetFormatsManagementOpened,
    initialSelectedFormat: props.initialSelectedFormat,
    reloadRecentlyUsedKey: props.reloadRecentlyUsedKey,
  });

  const getMenuStyles = React.useCallback(
    (css: React.CSSProperties) => ({
      ...css,
      width: undefined,
      left: "unset",
      right: 0,
    }),
    []
  );

  const getCurrentFormatSelectMenuListComponent = React.useCallback(
    (props: MenuListComponentProps<{}>) => {
      const isIdsContext = context === 1;
      const isPdsContext = context === 2;
      // In regular IDS views (formatOnly=false, context=1) show "Individual".
      // In pedigree split view we render Header in formatOnly mode even though
      // context may be 1; in that case show "Pedigree" per requirements.
      const pedigreeLabel =
        isIdsContext && !formatOnly
          ? "Set Current Format as Default for this Individual"
          : "Set Current Format as Default for this Pedigree";

      return (
        <components.MenuList
          {...props}
          className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__menu"
        >
          <h6 className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__title">
            {trDts("Recently Used Datasheet Formats")}
          </h6>

          {isRecentlyUsedLoading ? (
            <div className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__loading">
              {trDts("Loading")}
            </div>
          ) : (
            <>
              {/* Top: Persisted MRU from last session (fixed until close/reopen - GWT-style) */}
              {recentlyUsedFormats.map((format) => (
                  <div key={format.id}>
                    <Button
                      onClick={() => handleFormatSelect(format)}
                      className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
                    >
                      {format.name}
                    </Button>
                  </div>
                ))}
            </>
          )}
          <div className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-divider" />

          {/* Case A: Template Option OFF — show only "Set as Default for this Pedigree/Individual" */}
          {selectedFormat?.id && (isIdsContext || isPdsContext) && !templateOptionOn && (
            <Button
              onClick={() => {
                handleFormatAsTheDefaultDropdownChange(selectedFormat);
                if (onSetPedigreeDefaultFormat) {
                  const maybePromise = (onSetPedigreeDefaultFormat(
                    Number(selectedFormat.id)
                  ) as unknown) as Promise<void> | void;
                  if (maybePromise && typeof (maybePromise as any).then === "function") {
                    (maybePromise as Promise<void>).finally(() => {
                      handleDatasheetFormatsManagementClose();
                    });
                  } else {
                    handleDatasheetFormatsManagementClose();
                  }
                } else {
                  handleDatasheetFormatsManagementClose();
                }
              }}
              className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
            >
              {pedigreeLabel}
            </Button>
          )}

          {/* Case B: Template Option ON — show only "Set as Default for this Folder" (disabled for non-admins) */}
          {selectedFormat?.id && selectedFolder?.id && templateOptionOn && (
            <Button
              onClick={() => {
                handleFormatAsTheDefaultDropdownChange(selectedFormat);
                if (onSetFolderDefaultFormat && canSetFolderDefault) {
                  const maybePromise = (onSetFolderDefaultFormat({
                    folderId: selectedFolder.id as number,
                    formatId: Number(selectedFormat.id),
                  }) as unknown) as Promise<void> | void;
                  if (maybePromise && typeof (maybePromise as any).then === "function") {
                    (maybePromise as Promise<void>).finally(() => {
                      handleDatasheetFormatsManagementClose();
                    });
                  } else {
                    handleDatasheetFormatsManagementClose();
                  }
                } else {
                  handleDatasheetFormatsManagementClose();
                }
              }}
              disabled={!canSetFolderDefault}
              className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn--folder-default"
            >
              {"Set Current Format as Default for this Folder"}
            </Button>
          )}
          {/* Bottom: Newly selected this session (GWT-style: no divider after Set Default) */}
          {sessionFormats.map((format) => (
            <div key={`session-${format.id}`}>
              <Button
                onClick={() => handleFormatSelect(format)}
                className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
              >
                {format.name}
              </Button>
            </div>
          ))}
        </components.MenuList>
      );
    },
    [
      selectedFormat?.id,
      selectedFolder?.id,
      handleFormatAsTheDefaultDropdownChange,
      handleFormatSelect,
      onSetFolderDefaultFormat,
      onSetPedigreeDefaultFormat,
      canSetFolderDefault,
      context,
      templateOptionOn,
      formatOnly,
      recentlyUsedFormats,
      sessionFormats,
      isRecentlyUsedLoading,
    ]
  );

  const getMenuListComponent = (props: MenuListComponentProps<{}>) => {
    return (
      <components.MenuList {...props} className="DatasheetsResultHeader__formats__menu">
        <ClickAwayListener onClickAway={handleFormatAsTheDefaultDropdownClose}>
          <Dropdown
            className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown"
            fullWidth
            searchable={false}
            selectedValue={selectedFormat?.name || DROPDOWN_NO_VALUE}
            menuListComponent={getCurrentFormatSelectMenuListComponent}
            onBlur={handleFormatAsTheDefaultDropdownClose}
            onMenuClose={handleFormatAsTheDefaultDropdownClose}
          />
        </ClickAwayListener>
        <FormatFieldSelector
          folders={folders as Array<FolderPrimaryListDto>}
          selectedFolder={selectedFolder as FolderPrimaryListDto}
          isFoldersLoading={isFoldersLoading}
          selectedFormat={selectedFormat}
          onSelectedFolderChange={handleSelectedFolderChange}
          formats={folderFormats}
          isFormatsLoading={isFolderFormatsLoading}
          onSelectedFormatChange={handleFormatSelect}
          moduleName={DATASHEETS_MODULE_NAME.result}
          showTableHeader={false}
          classes={{
            folders: "DatasheetsResultHeader__format-management__folders",
            formats: "DatasheetsResultHeader__format-management__formats",
          }}
          tableColumns={[
            {
              label: trDts("Format Name"),
              field: FormatColumnKey.name,
            },
          ]}
        />
      </components.MenuList>
    );
  };

  const getControlComponent = React.useCallback(
    ({ cx, children, ...rest }: ControlProps<{}>) => (
      <components.Control cx={() => {}} {...rest} className="DatasheetsResultHeader__formats__menu-control">
        <Button
          variant="text"
          className="DatasheetsResultHeader__formats-btn"
          onClick={
            datasheetFormatsManagementOpened
              ? handleDatasheetFormatsManagementClose
              : handleDatasheetFormatsManagementOpen
          }
        >
          <i className="fa fa-edit" />
        </Button>
      </components.Control>
    ),
    [datasheetFormatsManagementOpened, handleDatasheetFormatsManagementClose, handleDatasheetFormatsManagementOpen]
  );

  const shouldRenderHeader = !formatOnly || showFormatPicker;

  return (
    <>
      {!formatOnly && <MessageBox hideMessageBox={closeMessage} {...messageProps} />}
      {shouldRenderHeader && (
        <Flex
          className="DatasheetsResultHeader"
          alignItems="center"
          justify={formatOnly ? "flex-end" : "space-between"}
        >
          {!formatOnly && <ActionsBase classes={{ item: "DatasheetsResultHeader__item" }} items={actions} />}
          {/* {showFormatPicker && (
            <Flex alignItems="center">
              <ClickAwayListener onClickAway={handleDatasheetFormatsManagementClose}>
                <Dropdown
                  className="DatasheetsResultHeader__formats-dropdown"
                  fullWidth
                  searchable={false}
                  menuIsOpen={datasheetFormatsManagementOpened}
                  menuListComponent={getMenuListComponent}
                  controlComponent={getControlComponent}
                  styles={{
                    width: "100%",
                    menu: getMenuStyles,
                    menuPortal: getMenuPortalStyles,
                  }}
                  onBlur={handleDatasheetFormatsManagementClose}
                  onMenuClose={handleDatasheetFormatsManagementClose}
                  // External open support: when parent requests it, open/close is handled via state in the controller
                  menuPortalTarget={undefined}
                />
              </ClickAwayListener>
            </Flex>
          )} */}
        </Flex>
      )}
    </>
  );
};
